import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectCapabilities } from '../../redux/capabilities/selectors';
import { selectCurrentEmail } from '../../redux/auth/selectors';
import type { SelfCapabilities } from '../../service/capability.service';
import { useCapability, useNodeCapabilities } from './useSelfCapabilities';
import {
  administrability,
  type AdministrableTarget,
  type Administrability
} from '../../service/userLifecycle.service';

/**
 * What the signed-in caller may do on the user administration screen.
 *
 * Both permissions below resolve from `PLATFORM_USER`, which is the module the server
 * actually gates every one of these routes on — deactivate, reactivate, reset password and
 * clear MFA included (`user_admin_router.py`, `mfa_router.py`).
 *
 * This used to ask for a separate lifecycle module, splitting "switch an account off, set its
 * password, clear its second factor" from "create, delete, change a role". That split does
 * not exist in the backend's `Module` enum, and `capabilities/me` returns one key per
 * module — so the name was absent from every response, `useCapability` resolved it to
 * false for every caller in every workspace, and the Edit User dialog refused everyone with
 * "Your role does not permit administering users in this workspace", a Superadmin included.
 * Gating the UI on a capability the server does not enforce is how that goes unnoticed:
 * nothing 403s, the control simply never appears.
 *
 * If the split is wanted, it belongs in the backend first — a `Module` member, a
 * `DEFAULT_MATRIX` row, and those four routes re-gated — and this hook follows it. Until
 * then the UI mirrors the gate that exists.
 *
 * The caller's own tier comes from the workspace capability row (`role`), not from
 * `auth.current.roles`: the login response answers `roles: []`, so gating on it would put
 * every caller at the bottom tier.
 *
 * A console passes its own node so the answer is the caller's row there, not the (possibly absent) active workspace's.
 */
export const useUserAdministration = (nodeId?: number) => {
  const capabilities = useSelector(selectCapabilities) as SelfCapabilities | null;
  const callerEmail = useSelector(selectCurrentEmail) as string | undefined;

  /**
   * Lifecycle actions: deactivate, reactivate, reset password, clear MFA. Bounded further
   * by the strictly-downward tier rule in `administrability`.
   */
  const canAdministerLifecycle = useCapability('PLATFORM_USER', 'manage', nodeId);
  /** Create, delete, and role changes. */
  const canManageAccounts = useCapability('PLATFORM_USER', 'manage', nodeId);

  const nodeRow = useNodeCapabilities(nodeId);
  const callerRole = (nodeId != null ? nodeRow?.role : capabilities?.role) ?? null;

  const administrabilityOf = useCallback(
    (target: AdministrableTarget): Administrability =>
      administrability({
        capabilityHeld: canAdministerLifecycle,
        callerRole,
        callerEmail,
        target
      }),
    [canAdministerLifecycle, callerRole, callerEmail]
  );

  return {
    canAdministerLifecycle,
    canManageAccounts,
    callerRole,
    callerEmail,
    administrabilityOf
  };
};

export default useUserAdministration;
